import instance from './instance';
import timeoutInstance from './timeoutInstace';
import {
    LibraryResponse,
    AssetDetailResponse,
    FetchComplianceDataParams,
    UploadStatusResponse,
    AnalyticsDataResponse,
    UploadState,
    UserDetails,
    AppliedFilter,
    CommentActivity,
    StatusChangeActivity,
    UploadActivity,
    FileFormat,
    ReviewData,
    ApprovalStatus,
    Outcome,
    AnalyticsResponse,
} from '../types';

import { MutableRefObject } from 'react';
import { LibraryContextType } from '../contexts/LibraryContext';

export const fetchAccessToken = async (code: string) => {
    try {
        await instance.get(`/access_token?code=${code}`);
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchSymbolUrl = async (symbolUrl: string): Promise<string> => {
    try {
        const response = await instance.get(`/symbol/${symbolUrl}`);
        if (response.data) {
            return response.data;
        } else {
            throw new Error('Item not found');
        }
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const validateToken = async () => {
    try {
        await instance.get('/validate_token');
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchAssetDetails = async (
    id: string,
): Promise<AssetDetailResponse> => {
    try {
        const response = await instance.get(`/assets/${id}`);
        if (response.data) {
            return response.data;
        } else {
            throw new Error('Item not found');
        }
    } catch (error) {
        console.error('Error fetching item detail:', error);
        throw error;
    }
};

export const submitFeedbackForItem = async (
    id: string,
    ruleId: string,
    feedbackdata: {
        comment: string;
        new_outcome: string;
    },
): Promise<AssetDetailResponse> => {
    try {
        const response = await instance.post<AssetDetailResponse>(
            `/feedback/${id}/${ruleId}`,
            feedbackdata,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating item feedback:', error);
        throw error;
    }
};

export const deleteAsset = async (asset_id: string) => {
    // todo update to pass in actual item id
    try {
        const response = await instance.delete(`/assets/${asset_id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting asset:', error);
        throw error;
    }
};

export const fetchConfigData = async () => {
    try {
        const response = await instance.get(`/config`);
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchUploadStatus = async (): Promise<UploadStatusResponse[]> => {
    try {
        const response = await timeoutInstance.get(`/upload_status`); // todo change back
        return response.data;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const removeImageFromUploadStatus = async (image_id: string) => {
    // todo update to pass in actual item id
    try {
        instance.post(`/upload_status/${image_id}/remove`);
    } catch (error) {
        console.error('Error removing image:', error);
        throw error;
    }
};

export const fetchLibraryData = async ({
    pageNumber = 1,
    sortField = 'id',
    sortOrder = 'ASC',
    search,
    numItems,
    filters,
}: FetchComplianceDataParams): Promise<LibraryResponse> => {
    try {
        const params: Record<string, string | number> = {
            page_number: pageNumber,
            page_size: numItems,
            sort_field: sortField,
            sort_order: sortOrder,
            filters: JSON.stringify(filters),
        };

        if (search) {
            params.search = search;
        }

        const response = await instance.get(`/assets`, { params });

        return response.data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching compliance data');
    }
};

export const fetchAnalyticsData = async (): Promise<AnalyticsResponse> => {
    try {
        const response = await instance.get<AnalyticsResponse>(`/analytics`);
        return response.data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching analytics data');
    }
};

export const waitForWebSocketMessage = (
    websocket: MutableRefObject<WebSocket | null>,
    imageId: string,
    timeout: number, // default timeout of 30 seconds
): Promise<void> => {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(new Error('Timeout waiting for WebSocket message'));
        }, timeout);

        const messageListener = (event: MessageEvent) => {
            if (event.data === `success, ${imageId}`) {
                clearTimeout(timeoutId);
                websocket.current?.removeEventListener(
                    'message',
                    messageListener,
                );
                resolve();
            } else if (event.data === `error, ${imageId}`) {
                clearTimeout(timeoutId);
                websocket.current?.removeEventListener(
                    'message',
                    messageListener,
                );
                reject(new Error('Error from websocket'));
            }
        };

        websocket.current?.addEventListener('message', messageListener);

        // Handle WebSocket closure
        websocket.current?.addEventListener('close', () => {
            clearTimeout(timeoutId);
            reject(new Error('WebSocket closed before receiving the message'));
        });
    });
};


export const fetchUploadToken = async (
    fileFormat: FileFormat,
    assetName: string,
) => {
    const payload = {
        file_format: fileFormat,
        asset_name: assetName,
    };
    try {
        const { data } = await instance.post<{
            url: string;
            unique_id: string;
        }>('/assets/uploads', payload);
        return data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching upload token');
    }
};

export const fetchUploadState = async () => {
    try {
        const { data } = await instance.get<UploadState[]>('/assets/uploads');
        return data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching upload state');
    }
};

export const reviewAsset = async (
    assetId: string,
    reviewData: ReviewData,
): Promise<void> => {
    try {
        const response = await instance.put<void>(
            `/assets/${assetId}`,
            reviewData,
        );
        return response.data;
    } catch (error) {
        console.error('Error processing specification:', error);
        throw error;
    }
};

export const postComment = async (
    assetId: string,
    comment: { content: string },
): Promise<(StatusChangeActivity | CommentActivity | UploadActivity)[]> => {
    try {
        const response = await instance.post<
            (StatusChangeActivity | CommentActivity | UploadActivity)[]
        >(`/assets/${assetId}/comments`, comment);
        return response.data;
    } catch (error) {
        console.error('Error posting comment:', error);
        throw error;
    }
};

export const changeApprovalStatus = async (
    assetId: string,
    statusChange: { new_status: ApprovalStatus; reason?: string },
): Promise<AssetDetailResponse> => {
    try {
        const response = await instance.patch<AssetDetailResponse>(
            `/assets/${assetId}/status`,
            statusChange,
        );
        return response.data;
    } catch (error) {
        console.error('Error changing approval status:', error);
        throw error;
    }
};

export const provideUserFeedback = async (
    assetId: string,
    ruleId: string,
    ruleFeedback: { reason: string },
): Promise<AssetDetailResponse> => {
    try {
        const response = await instance.patch<AssetDetailResponse>(
            `/assets/${assetId}/rules/${ruleId}/feedback`,
            ruleFeedback,
        );
        return response.data;
    } catch (error) {
        console.error('Error providing feedback:', error);
        throw error;
    }
};

export const patchHideUploadState = async (
    assetId: string,
    display: boolean,
): Promise<UploadState[]> => {
    const payload = {
        display: display,
    };
    try {
        const response = await instance.patch<UploadState[]>(
            `/assets/uploads/${assetId}`,
            payload,
        );
        return response.data;
    } catch (error) {
        console.error('Error processing specification:', error);
        throw error;
    }
};

export const getAssetOrgSlug = async (assetId: string): Promise<string> => {
    try {
        const response = await instance.get<string>(`/assets/${assetId}/org`);
        return response.data;
    } catch (error) {
        console.error('Error processing:', error);
        throw error;
    }
};

export const fetchUserDetails = async (
    userId: string,
): Promise<UserDetails | null> => {
    try {
        const response = await instance.get(`/user/${userId}`);
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

const convertSortOrder = (
    order: 'ascending' | 'descending',
): 'ASC' | 'DESC' => {
    switch (order) {
        case 'ascending':
            return 'ASC';
        case 'descending':
            return 'DESC';
        default:
            return 'ASC';
    }
};

export const handleLibraryRequest = async (
    libraryContext: LibraryContextType,
    newPageNumber?: number,
    showSpinner = true,
) => {
    try {
        if (showSpinner) {
            libraryContext.setters.setLibraryIsLoading(true);
        }
        const sortField = libraryContext.state.appliedSortOption.variable;
        const sortOrderValue = libraryContext.state.appliedSortOption.sortOrder;
        const sqlSortOrder = convertSortOrder(sortOrderValue);
        const pageNumber =
            newPageNumber ?? libraryContext.state.currentPage ?? 1;
        const params: {
            pageNumber: number;
            numItems: string;
            filters: AppliedFilter[];
            sortField: string;
            sortOrder: 'ASC' | 'DESC';
            search?: string;
        } = {
            pageNumber: pageNumber,
            numItems: libraryContext.state.numItems.toString(),
            filters: libraryContext.state.appliedFilters,
            sortField: sortField,
            sortOrder: sqlSortOrder,
        };

        if (libraryContext.state.searchString.length > 0) {
            params.search = libraryContext.state.searchString;
        }
        libraryContext.setters.setCurrentPage(pageNumber);
        libraryContext.setters.setAppliedFilters(
            libraryContext.state.appliedFilters,
        );
        const response = await fetchLibraryData(params);
        libraryContext.setters.setLibraryAssets(response.assets);
        libraryContext.setters.setPrevPage(response.prev_page || null);
        libraryContext.setters.setNextPage(response.next_page || null);
        libraryContext.setters.setTotalPages(response.total_pages);
        libraryContext.setters.setImageUrls(response.image_urls);
        libraryContext.setters.setFilterCounts(response.outgoing_filters);
    } catch (error) {
        console.error(error);
    } finally {
        libraryContext.setters.setLibraryIsLoading(false);
    }
};

// export const uploadAsset = async (
//     file: File,
//     uploadItem: FileUploadItem,
//     setUploadProgressArray: (
//         value: React.SetStateAction<UploadProgress[]>,
//     ) => void,
//     uploadProgressArrayRef: React.MutableRefObject<UploadProgress[]>,
//     newUploadProgress: UploadProgress,
// ): Promise<void> => {
//     setUploadProgressArray([
//         ...uploadProgressArrayRef.current,
//         newUploadProgress,
//     ]);
//     const uploadData = await fetchUploadData();

//     try {
//         const assetId = uploadData.asset_id;
//         newUploadProgress.imageId = assetId;
//         uploadItem.image_id = assetId;
//         setUploadProgressArray(
//             uploadProgressArrayRef.current.map((item) =>
//                 item.imageUrl === newUploadProgress.imageUrl
//                     ? { ...item, progress: 0, status: 'upload' }
//                     : item,
//             ),
//         );

//         // const blob = await rotateImage(file);
//         if (file) {
//             await axios.put(uploadData.url, file, {
//                 headers: { 'x-ms-blob-type': 'BlockBlob' },
//                 onUploadProgress: (progressEvent) => {
//                     const total = progressEvent.total ?? 1;
//                     const percentage = Math.round(
//                         (progressEvent.loaded * 100) / total,
//                     );
//                     setUploadProgressArray(
//                         uploadProgressArrayRef.current.map((item) =>
//                             item.imageId === uploadData.asset_id
//                                 ? { ...item, progress: percentage }
//                                 : item,
//                         ),
//                     );
//                 },
//             });
//         } else {
//             throw new Error('Blob generation failed.');
//         }
//     } catch (error) {
//         console.error('Upload failed:', error);
//         throw new Error('Image upload failed.');
//     }
//     setUploadProgressArray(
//         uploadProgressArrayRef.current.map((item) =>
//             item.imageId === uploadData.asset_id
//                 ? {
//                       ...item,
//                       status: 'checking',
//                       progress: 0,
//                       timeStamp: new Date(),
//                   }
//                 : item,
//         ),
//     );

// try {
//     const result: UploadResult = (
//         await instance.post('/upload_file_meta', uploadItem)
//     ).data;

//     console.log('response', result);
// } catch (error) {
//     console.error('Metadata upload failed:', error);
//     setUploadProgressArray((prevProgressArray) =>
//         prevProgressArray.map((item) =>
//             item.imageId === uploadData.image_id
//                 ? { ...item, status: 'error', progress: 100 }
//                 : item,
//         ),
//     );
// }
// };
